/**
 * ENG-10724 - should a CLI self-update be held on this host's `~/.claude.json`?
 *
 * Extracted from `manager-worker.ts` on the sixth review round of #6250, for the
 * reason Obi Wan gave: every assertion about this predicate was an AST SHAPE
 * check - that it reads the auth mode, that the mode guard is `!==`, that a
 * darwin branch exists. Swap the two returns in the subscription branch and the
 * predicate inverts - every paired host stops holding and every unpaired host
 * latches the permanent hold it exists to prevent - and the whole suite stayed
 * green. A predicate the entire fix rests on, wrong in two prior rounds, tested
 * only by its silhouette. It lives here so its DECISION can be tested, with the
 * three inputs injected.
 *
 * THE QUESTION. `checkAndUpdateCli` runs on every host unconditionally, where
 * ENG-10351's `deviceStateAllowsRollover` is only ever reached from the teardown
 * of a live session - so that gate carried an implicit "this host is set up for
 * Claude Code" precondition that the self-update path does not. Without one, a
 * bootstrapped-but-unpaired host latches a host-wide hold within five minutes of
 * manager start, opens a critical team-less alert on the global ops ladder every
 * heartbeat, and refuses every CLI upgrade including an urgent hotfix - for a
 * file the pairing flow has not written yet.
 *
 * `hosts.claude_auth_mode` is the authority, and it is host-level by
 * construction ("every agent on the host shares it"):
 *
 *   - `api_key` / `openrouter`: Claude Code runs and still needs
 *     `~/.claude.json` for onboarding state (ENG-4633). There is no pairing step
 *     to be "before", and api_key mode DELETES the credentials file
 *     ("preventing OAuth fallback"), so their absence says nothing. HOLD.
 *   - `subscription`: pairing writes `.credentials.json` AND `~/.claude.json`
 *     (ENG-4633 is the fix for a flow that wrote only the first and left hosts
 *     on the login picker with valid tokens sitting there). Credentials present
 *     = paired and the device state has been lost = HOLD. Absent = never paired,
 *     nothing to protect.
 *   - unknown (`null`, before the exchange is cached): HOLD.
 *
 * EVERY UNKNOWN FAILS CLOSED, because the asymmetry is stark: a hold taken
 * wrongly is cleared by pairing the host, which is what the alert already tells
 * the operator to do, while a hold skipped wrongly takes every agent on the host
 * out until a human notices.
 *   - macOS keeps credentials in the Keychain rather than a file, so the
 *     subscription check cannot answer there.
 *   - A stat that fails for any reason OTHER than "no such file" is an unknown,
 *     not an answer. ENOENT is a genuine "absent"; EACCES/EPERM/EIO are "cannot
 *     tell", and treating them as absent was fail-OPEN in a predicate documented
 *     as failing closed (Obi Wan on #6250). A `~/.claude` left at 0700 by an
 *     earlier root-run manager is enough to produce one.
 *
 * NO FRAMEWORK FILTER, deliberately. An earlier version skipped the hold when
 * the manager's per-agent framework cache held no claude-code agent. That cache
 * fills one agent at a time inside the per-agent sweep while `checkAndUpdateCli`
 * runs fire-and-forget from the top of the same cycle, so on a mixed
 * opencode/claude-code host a partially-populated cache read as "no claude-code
 * agents", skipped the hold, and parked the claude-code agents on the restart.
 * Nothing process-local closes that window.
 *
 * CREDENTIAL GUARDRAIL. Existence only. `statSync` answers "is there a file";
 * nothing here is opened, parsed, logged or returned. `claude-device-state.ts`
 * promises `.credentials.json` is never touched and that promise is why this is
 * a sibling module rather than an amendment to it.
 */
import { statSync } from 'node:fs';
import { platform as osPlatform } from 'node:os';

export type ClaudeHostAuthMode = 'subscription' | 'api_key' | 'openrouter';

/** Why the predicate decided as it did. For logs and tests; never a credential. */
export type DeviceStateHoldApplicability =
  | { applies: true; because: 'non-subscription-mode' | 'mode-unknown' | 'platform-opaque' | 'paired' | 'credentials-unreadable' }
  | { applies: false; because: 'never-paired' };

export interface DeviceStateHoldInputs {
  /** `hosts.claude_auth_mode`, or null before the exchange is cached. */
  authMode: ClaudeHostAuthMode | null;
  /** Candidate credential paths, in preference order. Stat'd, never read. */
  credentialPaths: readonly string[];
  /** Injected for tests. Defaults to the real platform. */
  platform?: () => string;
  /** Injected for tests. Defaults to a real existence stat. */
  statPath?: (path: string) => { isFile: boolean } | { errno: string };
}

function realStat(path: string): { isFile: boolean } | { errno: string } {
  try {
    return { isFile: statSync(path).isFile() };
  } catch (err) {
    return { errno: (err as NodeJS.ErrnoException)?.code ?? 'UNKNOWN' };
  }
}

/**
 * Whether an unusable `~/.claude.json` MATTERS on this host. The gate answers
 * whether the file is usable; this answers whether anyone should care.
 */
export function deviceStateHoldApplies(inputs: DeviceStateHoldInputs): DeviceStateHoldApplicability {
  const { authMode, credentialPaths } = inputs;
  const platform = inputs.platform ?? osPlatform;
  const statPath = inputs.statPath ?? realStat;

  if (authMode === null) return { applies: true, because: 'mode-unknown' };
  if (authMode !== 'subscription') return { applies: true, because: 'non-subscription-mode' };
  if (platform() === 'darwin') return { applies: true, because: 'platform-opaque' };

  let sawUnknownError = false;
  for (const path of credentialPaths) {
    const result = statPath(path);
    if ('isFile' in result) {
      if (result.isFile) return { applies: true, because: 'paired' };
      continue;
    }
    // ENOENT is a real answer: this path is absent, keep looking. Anything else
    // means we could not tell, which is not the same thing.
    if (result.errno !== 'ENOENT') sawUnknownError = true;
  }
  if (sawUnknownError) return { applies: true, because: 'credentials-unreadable' };
  return { applies: false, because: 'never-paired' };
}
